/**
 * Platform (staff) roles and permissions: a separate namespace from org permissions
 * (docs/03 "Platform permissions").
 */
export const PLATFORM_ROLES = ['support', 'network_admin', 'finance_admin', 'super_admin'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export const PLATFORM_PERMISSIONS = [
  'platform:read',
  'platform:impersonate',
  'platform:orgs:manage',
  'platform:directory:manage',
  'platform:blacklist:manage',
  'platform:verification:review',
  'platform:community:manage',
  'platform:content:manage',
  'platform:disputes:review',
  'platform:plans:manage',
  'platform:billing:manage',
  'platform:tariff:manage',
  'platform:settlement:oversee',
  'platform:claims:decide',
  'platform:staff:manage',
  'platform:config:manage',
] as const;
export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[number];

const support: PlatformPermission[] = ['platform:read', 'platform:impersonate'];

export const PLATFORM_ROLE_PERMISSIONS: Readonly<Record<PlatformRole, ReadonlySet<PlatformPermission>>> = {
  support: new Set(support),
  network_admin: new Set<PlatformPermission>([
    ...support,
    'platform:orgs:manage',
    'platform:directory:manage',
    'platform:blacklist:manage',
    'platform:verification:review',
    'platform:community:manage',
    'platform:content:manage',
    'platform:disputes:review',
  ]),
  finance_admin: new Set<PlatformPermission>([
    ...support,
    'platform:plans:manage',
    'platform:billing:manage',
    'platform:tariff:manage',
    'platform:settlement:oversee',
    'platform:disputes:review',
    'platform:claims:decide',
  ]),
  // "everything, plus staff:manage and config:manage"
  super_admin: new Set(PLATFORM_PERMISSIONS),
};

const PLATFORM_PERMISSION_SET: ReadonlySet<string> = new Set(PLATFORM_PERMISSIONS);

export function isPlatformPermission(value: unknown): value is PlatformPermission {
  return typeof value === 'string' && PLATFORM_PERMISSION_SET.has(value);
}

export function isPlatformRole(value: unknown): value is PlatformRole {
  return typeof value === 'string' && (PLATFORM_ROLES as readonly string[]).includes(value);
}
